// src/components/learning/islamic/pillarsData.js
// Les 5 Piliers de l'Islam - Contenu éducatif pour enfants

export const pillarsData = [
  {
    id: 1,
    number: "١",
    name: "Shahada",
    nameAr: "الشَّهَادَة",
    title: "Le Témoignage de Foi",
    icon: "☝️",
    color: "#8b5cf6",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
    shortDescription: "Déclarer sa foi en Allah et en Son messager",
    
    // Contenu détaillé
    content: {
      intro: "La Shahada est la porte d'entrée dans l'Islam. C'est une déclaration sincère du cœur.",
      
      arabicText: "أَشْهَدُ أَنْ لَا إِلَٰهَ إِلَّا اللَّهُ وَأَشْهَدُ أَنَّ مُحَمَّدًا رَسُولُ اللَّهِ",
      
      transliteration: "Ash-hadu an la ilaha illa Allah, wa ash-hadu anna Muhammadan rasul Allah",
      
      translation: "J'atteste qu'il n'y a pas de divinité en dehors d'Allah, et j'atteste que Muhammad est le messager d'Allah",
      
      parts: [
        {
          arabic: "لَا إِلَٰهَ إِلَّا اللَّهُ",
          meaning: "Il n'y a pas de dieu sauf Allah",
          explanation: "On croit en un seul Dieu, Allah, le Créateur de tout"
        },
        {
          arabic: "مُحَمَّدٌ رَسُولُ اللَّهِ",
          meaning: "Muhammad est le messager d'Allah",
          explanation: "Le Prophète Muhammad ﷺ nous a transmis le message d'Allah"
        }
      ],
      
      funFacts: [
        "🌟 C'est la première chose qu'un bébé musulman entend à sa naissance !",
        "💫 C'est aussi la dernière chose qu'on dit avant de mourir",
        "🤲 On la répète dans chaque prière !"
      ],
      
      activity: {
        type: "repeat",
        instruction: "Répète après moi, mot par mot :",
        words: ["Ash-hadu", "an la", "ilaha", "illa", "Allah"]
      }
    },
    
    quiz: [
      {
        question: "Combien de parties a la Shahada ?",
        options: ["1", "2", "3", "5"],
        correct: "2",
        explanation: "La Shahada a 2 parties : croire en Allah et croire en Son messager ﷺ"
      },
      {
        question: "Qui est le messager d'Allah mentionné dans la Shahada ?",
        options: ["Ibrahim", "Musa", "Muhammad ﷺ", "Issa"],
        correct: "Muhammad ﷺ",
        explanation: "Le Prophète Muhammad ﷺ est le dernier messager d'Allah"
      }
    ]
  },

  {
    id: 2,
    number: "٢",
    name: "Salat",
    nameAr: "الصَّلَاة",
    title: "La Prière",
    icon: "🤲",
    color: "#3b82f6",
    gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    shortDescription: "Prier 5 fois par jour pour se connecter à Allah",
    
    content: {
      intro: "La Salat est notre conversation directe avec Allah. 5 fois par jour, on se tourne vers Lui !",
      
      prayers: [
        { name: "Fajr", nameAr: "الفَجْر", time: "🌅 À l'aube", rakaat: 2, emoji: "🌄" },
        { name: "Dhuhr", nameAr: "الظُّهْر", time: "☀️ À midi", rakaat: 4, emoji: "🌞" },
        { name: "Asr", nameAr: "العَصْر", time: "🌤️ L'après-midi", rakaat: 4, emoji: "⛅" },
        { name: "Maghrib", nameAr: "المَغْرِب", time: "🌅 Au coucher du soleil", rakaat: 3, emoji: "🌇" },
        { name: "Isha", nameAr: "العِشَاء", time: "🌙 La nuit", rakaat: 4, emoji: "🌙" }
      ],
      
      funFacts: [
        "🕌 Les musulmans du monde entier se tournent vers la Kaaba à La Mecque",
        "🧮 En une journée, on fait 17 rakaat obligatoires !",
        "🌍 À chaque instant, des musulmans prient quelque part sur Terre",
        "💪 La prière nous donne de la force et de la paix"
      ],
      
      totalRakaat: "17 rakaat par jour = 17 conversations avec Allah !",
      
      activity: {
        type: "match",
        instruction: "Associe chaque prière à son moment :",
        items: [
          { prayer: "Fajr", time: "À l'aube" },
          { prayer: "Maghrib", time: "Au coucher du soleil" }
        ]
      }
    },
    
    quiz: [
      {
        question: "Combien de prières fait-on chaque jour ?",
        options: ["3", "4", "5", "7"],
        correct: "5",
        explanation: "On prie 5 fois par jour : Fajr, Dhuhr, Asr, Maghrib et Isha"
      },
      {
        question: "Quelle prière fait-on au lever du soleil ?",
        options: ["Isha", "Fajr", "Dhuhr", "Maghrib"],
        correct: "Fajr",
        explanation: "Fajr (الفجر) signifie l'aube, c'est la prière du matin"
      }
    ]
  },

  {
    id: 3,
    number: "٣",
    name: "Zakat",
    nameAr: "الزَّكَاة",
    title: "L'Aumône Purificatrice",
    icon: "💝",
    color: "#10b981",
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    shortDescription: "Partager ses richesses avec ceux qui en ont besoin",
    
    content: {
      intro: "La Zakat, c'est partager ! Allah nous demande d'aider les autres avec une partie de ce qu'Il nous a donné.",
      
      meaning: {
        word: "Zakat",
        arabic: "زَكَاة",
        meanings: ["Purification", "Croissance", "Bénédiction"]
      },
      
      percentage: "2.5%",
      percentageExplained: "Sur chaque 100€ économisés pendant un an, on donne 2,50€ aux pauvres",
      
      recipients: [
        { icon: "👨‍👩‍👧‍👦", name: "Les pauvres", description: "Ceux qui n'ont pas assez pour vivre" },
        { icon: "🏠", name: "Les nécessiteux", description: "Ceux qui ont besoin d'aide" },
        { icon: "🎓", name: "Les étudiants", description: "Pour apprendre le Coran et la religion" },
        { icon: "🌍", name: "Les voyageurs", description: "Ceux qui sont bloqués loin de chez eux" }
      ],
      
      funFacts: [
        "💰 La Zakat purifie notre argent et notre cœur",
        "🌱 Quand on donne, Allah nous donne encore plus !",
        "🤝 C'est un moyen d'aider la communauté",
        "😊 Donner rend heureux celui qui donne ET celui qui reçoit"
      ],
      
      story: {
        title: "Le secret du partage",
        text: "Un jour, un compagnon du Prophète ﷺ a donné la moitié de ses biens. Le Prophète ﷺ lui a dit qu'Allah le récompenserait énormément. Et devinez quoi ? Sa richesse a été multipliée !"
      },
      
      activity: {
        type: "calculate",
        instruction: "Si tu as 40€ dans ta tirelire, combien donnerais-tu en Zakat ?",
        answer: "1€",
        hint: "2.5% de 40€ = 1€"
      }
    },
    
    quiz: [
      {
        question: "Quel pourcentage donne-t-on en Zakat ?",
        options: ["1%", "2.5%", "5%", "10%"],
        correct: "2.5%",
        explanation: "On donne 2.5% de nos économies chaque année"
      },
      {
        question: "À qui donne-t-on la Zakat ?",
        options: ["Aux riches", "Aux pauvres", "Au gouvernement", "À la banque"],
        correct: "Aux pauvres",
        explanation: "La Zakat aide les personnes dans le besoin"
      }
    ]
  },

  {
    id: 4,
    number: "٤",
    name: "Sawm",
    nameAr: "الصَّوْم",
    title: "Le Jeûne du Ramadan",
    icon: "🌙",
    color: "#f59e0b",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    shortDescription: "Jeûner pendant le mois béni du Ramadan",
    
    content: {
      intro: "Pendant le Ramadan, les musulmans ne mangent pas et ne boivent pas du lever au coucher du soleil. C'est un mois très spécial !",
      
      month: {
        name: "Ramadan",
        nameAr: "رَمَضَان",
        position: "9ème mois du calendrier islamique",
        duration: "29 ou 30 jours"
      },
      
      schedule: [
        { time: "🌅 Suhoor", description: "Repas avant l'aube", emoji: "🍽️" },
        { time: "☀️ Journée", description: "On ne mange pas, on ne boit pas", emoji: "💪" },
        { time: "🌙 Iftar", description: "On rompt le jeûne au coucher du soleil", emoji: "🥗" },
        { time: "🌟 Tarawih", description: "Prières spéciales le soir", emoji: "🤲" }
      ],
      
      benefits: [
        { icon: "❤️", text: "Se rapprocher d'Allah" },
        { icon: "🧠", text: "Apprendre la patience" },
        { icon: "🤝", text: "Comprendre les pauvres qui ont faim" },
        { icon: "💪", text: "Renforcer sa volonté" }
      ],
      
      specialNight: {
        name: "Laylat al-Qadr",
        nameAr: "لَيْلَةُ القَدْر",
        meaning: "La Nuit du Destin",
        description: "Une nuit qui vaut mieux que 1000 mois ! C'est pendant cette nuit que le Coran a été révélé."
      },
      
      funFacts: [
        "🌙 Le Ramadan commence quand on voit le nouveau croissant de lune",
        "🎉 À la fin du Ramadan, on fête l'Aïd al-Fitr !",
        "🍬 Les enfants ne sont pas obligés de jeûner, mais peuvent essayer",
        "📖 C'est le mois où le Coran a été révélé"
      ],
      
      activity: {
        type: "order",
        instruction: "Remets dans l'ordre une journée de Ramadan :",
        items: ["Suhoor (manger avant l'aube)", "Jeûner la journée", "Iftar (manger le soir)", "Tarawih (prière)"]
      }
    },
    
    quiz: [
      {
        question: "Pendant quel mois jeûne-t-on ?",
        options: ["Muharram", "Rajab", "Ramadan", "Dhul Hijja"],
        correct: "Ramadan",
        explanation: "Le Ramadan est le 9ème mois du calendrier islamique"
      },
      {
        question: "Comment s'appelle le repas du soir pendant le Ramadan ?",
        options: ["Suhoor", "Iftar", "Dîner", "Tarawih"],
        correct: "Iftar",
        explanation: "L'Iftar est le repas pour rompre le jeûne au coucher du soleil"
      }
    ]
  },

  {
    id: 5,
    number: "٥",
    name: "Hajj",
    nameAr: "الحَجّ",
    title: "Le Pèlerinage à La Mecque",
    icon: "🕋",
    color: "#1f2937",
    gradient: "linear-gradient(135deg, #374151 0%, #1f2937 100%)",
    shortDescription: "Voyager à La Mecque au moins une fois dans sa vie",
    
    content: {
      intro: "Le Hajj est un voyage extraordinaire ! Des millions de musulmans du monde entier se retrouvent à La Mecque pour adorer Allah ensemble.",
      
      location: {
        city: "La Mecque",
        cityAr: "مَكَّة المُكَرَّمَة",
        country: "Arabie Saoudite",
        monument: "La Kaaba",
        monumentAr: "الكَعْبَة"
      },
      
      when: {
        month: "Dhul Hijja",
        monthAr: "ذُو الحِجَّة",
        position: "12ème mois du calendrier islamique",
        days: "Du 8 au 12 Dhul Hijja"
      },
      
      rituals: [
        { 
          name: "Ihram", 
          nameAr: "إِحْرَام",
          icon: "👕",
          description: "Porter des vêtements blancs simples - tous égaux devant Allah"
        },
        { 
          name: "Tawaf", 
          nameAr: "طَوَاف",
          icon: "🔄",
          description: "Tourner 7 fois autour de la Kaaba"
        },
        { 
          name: "Sa'i", 
          nameAr: "سَعْي",
          icon: "🏃",
          description: "Marcher 7 fois entre Safa et Marwa"
        },
        { 
          name: "Arafat", 
          nameAr: "عَرَفَة",
          icon: "🏔️",
          description: "Prier sur le mont Arafat - le jour le plus important"
        },
        { 
          name: "Lapidation", 
          nameAr: "رَمْي الجَمَرَات",
          icon: "🪨",
          description: "Jeter des cailloux pour rejeter le diable"
        }
      ],
      
      eid: {
        name: "Aïd al-Adha",
        nameAr: "عِيد الأَضْحَى",
        meaning: "Fête du Sacrifice",
        description: "On fête en souvenir du sacrifice du Prophète Ibrahim. On partage de la viande avec les pauvres."
      },
      
      funFacts: [
        "🌍 Plus de 2 millions de personnes font le Hajj chaque année !",
        "👕 Tout le monde porte du blanc : riches et pauvres sont égaux",
        "🕋 La Kaaba est la première maison construite pour adorer Allah",
        "✈️ Avant, le voyage prenait des mois ! Maintenant quelques heures en avion"
      ],
      
      obligation: "Une fois dans sa vie, si on en a les moyens physiques et financiers",
      
      activity: {
        type: "journey",
        instruction: "Suis le parcours du pèlerin :",
        steps: ["Ihram", "Tawaf", "Sa'i", "Arafat", "Muzdalifa", "Mina", "Aïd"]
      }
    },
    
    quiz: [
      {
        question: "Où se trouve la Kaaba ?",
        options: ["Médine", "Jérusalem", "La Mecque", "Le Caire"],
        correct: "La Mecque",
        explanation: "La Kaaba se trouve à La Mecque, en Arabie Saoudite"
      },
      {
        question: "Combien de fois tourne-t-on autour de la Kaaba ?",
        options: ["3 fois", "5 fois", "7 fois", "10 fois"],
        correct: "7 fois",
        explanation: "Le Tawaf consiste à tourner 7 fois autour de la Kaaba"
      }
    ]
  }
];

// Messages d'encouragement pour le quiz
export const quizEncouragements = {
  correct: [
    "🌟 Mashallah ! Excellent !",
    "⭐ Bravo ! Tu connais bien ta religion !",
    "🎉 Super ! Allah est fier de toi !",
    "💫 Très bien ! Continue comme ça !",
    "🏆 Champion ! Tu apprends vite !"
  ],
  wrong: [
    "💪 Ce n'est pas grave, réessaie !",
    "🤔 Presque ! Regarde bien...",
    "📖 C'est en apprenant qu'on progresse !",
    "🌱 Tu vas y arriver, inch'Allah !"
  ]
};

// Badge final
export const pillarsBadge = {
  name: "Connais les 5 Piliers",
  nameAr: "أركان الإسلام",
  icon: "🏆",
  description: "Tu as appris les 5 piliers de l'Islam !",
  requirement: "Répondre correctement à 8/10 questions"
};

export default pillarsData;
